
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useActiveAffiliates, useAssignUserToAffiliate } from '@/hooks/useUserAssignment';
import { UnassignedUser } from '@/services/userAssignmentService';

interface BulkAssignDialogProps {
  users: UnassignedUser[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}

export const BulkAssignDialog = ({ users, open, onOpenChange, onComplete }: BulkAssignDialogProps) => {
  const [selectedAffiliateId, setSelectedAffiliateId] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(0);
  
  const { data: affiliates, isLoading: loadingAffiliates } = useActiveAffiliates({ enabled: open });
  const assignMutation = useAssignUserToAffiliate();

  const handleBulkAssign = async () => {
    if (!selectedAffiliateId || users.length === 0) return;

    const selectedAffiliate = affiliates?.find(a => a.id === selectedAffiliateId);
    if (!selectedAffiliate) return;

    setIsProcessing(true);
    setCompleted(0);
    setProgress(0);

    try {
      for (let i = 0; i < users.length; i++) {
        const user = users[i];
        
        try {
          await assignMutation.mutateAsync({
            userId: user.id,
            affiliateId: selectedAffiliateId,
            referralCode: selectedAffiliate.referral_code
          });
          
          setCompleted(i + 1);
          setProgress(((i + 1) / users.length) * 100);
          
          // Small delay to prevent overwhelming the server
          if (i < users.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } catch (error) {
          console.error(`Error assigning user ${user.id}:`, error);
          // Continue with next user even if one fails
        }
      }
      
      // Wait a moment before closing
      setTimeout(() => {
        onComplete();
        onOpenChange(false);
        setIsProcessing(false);
        setSelectedAffiliateId('');
        setProgress(0);
        setCompleted(0);
      }, 1000);
      
    } catch (error) {
      console.error('Bulk assignment error:', error);
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Bulk Assign Users</DialogTitle>
          <DialogDescription>
            Assign {users.length} selected users to an affiliate partner.
          </DialogDescription>
        </DialogHeader>
        
        {!isProcessing ? (
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="affiliate" className="text-right">
                Affiliate
              </Label>
              <div className="col-span-3">
                <Select value={selectedAffiliateId} onValueChange={setSelectedAffiliateId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an affiliate" />
                  </SelectTrigger>
                  <SelectContent>
                    {loadingAffiliates ? (
                      <SelectItem value="loading" disabled>Loading...</SelectItem>
                    ) : (
                      affiliates?.map((affiliate) => (
                        <SelectItem key={affiliate.id} value={affiliate.id}>
                          {affiliate.name} ({affiliate.referral_code})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-6 space-y-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Processing assignment... {completed} of {users.length} completed
              </p>
            </div>
            <Progress value={progress} className="w-full" />
          </div>
        )}
        
        <div className="flex justify-end gap-2">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleBulkAssign}
            disabled={!selectedAffiliateId || isProcessing}
          >
            {isProcessing ? 'Processing...' : `Assign ${users.length} Users`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
